import { useCallback } from "react";
import type { FieldPathByValue, FieldValues, PathValue, UseFormReturn } from "react-hook-form";
import { apiErrorMessage } from "@shared-kernel/domain/errors/api-error";
import { UPLOAD_FAILED_MESSAGE } from "@shared-kernel/primary/shared/upload-button/upload-failed-message";
import type { ApiActionOutcome } from "./use-api-action";

// Téléversement passé tel quel aux composants qui gardent le refus dans leur état (liste de pièces jointes, facture de
// remboursement) : `file => run(uploadDocument({ document: file, … }))`, sans feedback. Un champ de formulaire reçoit
// `(file: File) => void`, le hook ayant posé l'issue sur le champ
export type Uploader = (file: File) => Promise<ApiActionOutcome<string>>;

type UploadForm<T extends FieldValues> = Pick<UseFormReturn<T>, "setValue" | "setError" | "clearErrors">;

// Dans le hook d'action : pose sur un champ d'URL l'issue d'un téléversement. Le champ prend l'URL, ou affiche pourquoi l'API refuse
// le fichier (PDF invalide, trop volumineux…). Rend l'URL, null sur refus
export const useFieldUpload = <T extends FieldValues>({ setValue, setError, clearErrors }: UploadForm<T>) =>
  useCallback(
    async <F extends FieldPathByValue<T, string>>(
      field: F,
      upload: Promise<ApiActionOutcome<string>>,
      fallback: string = UPLOAD_FAILED_MESSAGE,
    ): Promise<string | null> => {
      clearErrors(field);
      const outcome = await upload;
      if (!outcome.ok) {
        setError(field, { type: "server", message: apiErrorMessage(outcome.error, fallback) });
        return null;
      }
      // FieldPathByValue garantit que le champ porte une string, ce que PathValue ne sait pas déduire
      setValue(field, outcome.value as PathValue<T, F>, { shouldDirty: true, shouldValidate: true });
      return outcome.value;
    },
    [setValue, setError, clearErrors],
  );
